/* eslint-disable unicorn/prefer-global-this */
// ✅ React & Related Hooks
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";

// ✅ Third-party Styles
import "react-quill/dist/quill.bubble.css";

// ✅ Custom Styles
import "./style.scss";

// ✅ Redux Actions
import ZohoIntegrationActions from "@Actions/integrations/zohoIntegrationActions";
import UnicommerceIntegrationActions from "@Actions/integrations/unicommerceIntegrationActions";
import ShopifyIntegrationActions from "@Actions/integrations/shopifyIntegrationActions";
import TallyIntegrationActions from "@Actions/integrations/tallyIntegrationActions";
import EinvoiceEwaybillIntegrationActions from "@Actions/integrations/einvoiceEwaybillActions";
import BusyIntegrationActions from "../../../../actions/integrations/busyIntegrationAction";
import CNActions from "@Actions/cnActions";
import AttachmentActions from "@Actions/attachmentActions";
import AnalyticsActions from "@Actions/application/analyticsActions";
import ActivityLogActions from "@Actions/activityLogActions";

// ✅ Helpers / Utilities
import Helpers from "@Apis/helpers";
import {
  handleFileChange,
  HideAdhocApprovals,
  getLinkedDocumentInfo,
} from "./helper";

// ✅ Custom Components – Credit Note
import ViewCreditNoteHeader from "./ViewCreditNoteHeader";
import ViewCreditNoteDetails from "./ViewCreditNoteDetails";
import ViewCreditNoteAddresses from "./ViewCreditNoteAddresses";
import ViewCreditNoteEInvoiceInfo from "./ViewCreditNoteEInvoiceInfo";
import ViewCreditNoteFooter from "./ViewCreditNoteFooter";
import CreditNoteLines from "./CreditNoteLines";

// ✅ Custom Components – Common / Reused
import ViewDocumentEwb from "@Components/Common/ViewDocumentEwb";
import ViewDocRightSection from "../../../Common/ViewDocRightSection";
import ViewLoadingSkull from "../../../Common/ViewLoadingSkull";
import PRZDrawer from "../../../Common/UI/PRZDrawer";

// ✅ Custom Components – Payments
import RecordOutgoingPayment from "../../../Payments/RecordPayment/RecordOutgoingPayment";
import LinkOutgoingPayment from "../../../Payments/LinkPayment/LinkOutgoingPayment";
import ViewIncomingPayment from "@Components/Sales/IncomingPayments/ViewIncomingPayment";

// ✅ Custom Components – E-Waybill
import EwaybillForm from "@Components/Sales/EinvoicingEwaybill/EwaybillList/EwaybillForm";

function ViewCreditNote({
  updateCNStatusLoading,
  updateCNWorkflowStepLoading,
  user,
  updateCNWorkflowStep,
  history,
  getCNByIdLoading,
  selectedCN,
  selectedCNId,
  updateCNStatus,
  deleteCNLoading,
  deleteCN,
  callback,
  getAttachmentByIdLoading,
  updateAttachmentLoading,
  syncSOLoading,
  syncSO,
  syncShopifySO,
  syncShopifySOLoading,
  syncCN,
  syncCNLoading,
  MONEY,
  downloadDocument,
  pushCreditNoteToBusy,
  pushCreditNoteToBusyLoading,
  cancelIRP,
  cancelIRPLoading,
  pushCnToIRP,
  pushCnToIRPLoading,
  showTriggerNode,
  syncTallyInvoiceLoading,
  syncTallyInvoice,
  getCNById,
  getCNByIdSuccess,
  getActivityLogSuccess,
  match,
  selectedAttachment,
  isQuickView,
  getAttachmentById,
  updateAttachment,
  priceMasking,
}) {
  const [isUserReady, setIsUserReady] = useState(false);
  const [currentUpdate, setCurrentUpdate] = useState("");
  const [isGetAttachment, setIsGetAttachment] = useState(false);
  const [showEWaybillFormDrawer, setShowEWaybillFormDrawer] = useState(false);
  const [sendForApprovalConfirmation, setSendForApprovalConfirmation] =
    useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancellationPop, setCancellationPop] = useState(false);
  const [cancellationReasonIRP, setCancellationReasonIRP] = useState("");
  const [showErrorIRN, setShowErrorIRN] = useState(false);
  const [cancellationRemark, setCancellationRemark] = useState("");
  const [cancellationRemarkPop, setCancellationRemarkPop] = useState(false);
  const [isReturnSlip, setIsReturnSlip] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [isUserReadyForPrefillData, setIsUserReadyForPrefillData] =
    useState(false);
  const [linkPayment, setLinkPayment] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showViewPayout, setShowViewPayout] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    if (selectedCNId && user) {
      getCNById(
        Helpers.getTenantEntityPermission(
          user?.user_tenants,
          Helpers.permissionEntities.CREDIT_NOTE,
          Helpers.permissionTypes.READ
        ).join(",") || user?.tenant_info?.tenant_id,
        selectedCNId
      );
      getActivityLogSuccess(null);
    }

    return () => {
      getCNByIdSuccess(null);
      getActivityLogSuccess(null);
    };
  }, [user]);

  useEffect(() => {
    const { creditNoteId, returnSlipId } = match.params;
    const tenantId = user?.tenant_info?.tenant_id;
    const permissionTenantId =
      Helpers.getTenantEntityPermission(
        user?.user_tenants,
        Helpers.permissionEntities.CREDIT_NOTE,
        Helpers.permissionTypes.READ
      ).join(",") || tenantId;

    if (tenantId && !isUserReady) {
      const currentUrl = window.location.href;

      if (currentUrl.includes("/sales/credit-note/view") && creditNoteId) {
        getCNById(permissionTenantId, creditNoteId);
      }

      if (currentUrl.includes("/sales/return-slip/view") && returnSlipId) {
        getCNById(permissionTenantId, returnSlipId, "RETURN_SLIP");
      }

      setIsUserReady(true);
      setIsReturnSlip(currentUrl.includes("/sales/return-slip/view"));
    }
  }, [user, match]);

  useEffect(() => {
    if (selectedCN && !isUserReadyForPrefillData && isUserReady) {
      const currentCN = selectedCN?.data?.credit_notes?.[0];
      setFileList(currentCN?.attachments || []);
      setIsUserReadyForPrefillData(true);
    }
  }, [selectedCN, isUserReadyForPrefillData, isUserReady]);

  useEffect(() => {
    if (
      selectedAttachment &&
      isGetAttachment &&
      !getAttachmentByIdLoading &&
      !updateAttachmentLoading
    ) {
      setFileList(selectedAttachment);
      setIsGetAttachment(false);
    }
  }, [selectedAttachment, isGetAttachment]);

  const currentCN = useMemo(
    () => selectedCN?.data?.credit_notes?.[0],
    [selectedCN]
  );
  const isTallyConnected =
    currentCN?.tally_configuration?.credit_note_voucher_integration &&
    ["CONFIRMED"]?.includes(currentCN?.status);

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } =
    priceMasking;

  const fixedMenuBar = user?.side_menu_bar_type === "FIXED";

  const reasonOptions = [
    { label: "Duplicate", value: "DUPLICATE" },
    { label: "Order Cancelled", value: "ORDER CANCELLED" },
    { label: "Data Entry Mistake", value: "DATA ENTRY MISTAKE" },
    { label: "Others", value: "OTHERS" },
  ];

  return (
    <div
      className={`view-document__wrapper ${isQuickView ? "" : "view-document__wrapper-page"
        }`}
    >
      <PRZDrawer
        open={linkPayment}
        width="820px"
        onClose={() => setLinkPayment(false)}
        headerWidth="770px"
        title="Link Payment"
      >
        <LinkOutgoingPayment
          selectedEntity={currentCN}
          callback={() => {
            getCNById(
              Helpers.getTenantEntityPermission(
                user?.user_tenants,
                Helpers.permissionEntities.CREDIT_NOTE,
                Helpers.permissionTypes.READ
              ).join(",") || user?.tenant_info?.tenant_id,
              currentCN.cn_id
            );
            setLinkPayment(false);
          }}
          linkPaymentFromCN
        />
      </PRZDrawer>
      <PRZDrawer
        open={showPayment}
        width="420px"
        onClose={() => setShowPayment(false)}
        headerWidth="370px"
        title="Record New Payment"
      >
        <RecordOutgoingPayment
          selectedCN={currentCN}
          callback={() => {
            getCNById(
              Helpers.getTenantEntityPermission(
                user?.user_tenants,
                Helpers.permissionEntities.CREDIT_NOTE,
                Helpers.permissionTypes.READ
              ).join(",") || user?.tenant_info?.tenant_id,
              currentCN.cn_id
            );
            setShowPayment(false);
          }}
        />
      </PRZDrawer>
      <PRZDrawer
        open={showViewPayout}
        onClose={() => setShowViewPayout(false)}
        width="780px"
        headerWidth="730px"
        title={`View Payment #${selectedPayment?.documentId}`}
      >
        <ViewIncomingPayment
          selectedPaymentId={selectedPayment?.documentId}
          paymentType={selectedPayment?.applied_payment_type}
          onClose={() => {
            setShowViewPayout(false);
          }}
          callback={() => {
            getCNById(
              Helpers.getTenantEntityPermission(
                user?.user_tenants,
                Helpers.permissionEntities.CREDIT_NOTE,
                Helpers.permissionTypes.READ
              ).join(",") || user?.tenant_info?.tenant_id,
              currentCN.cn_id
            );
            setShowViewPayout(false);
          }}
        />
      </PRZDrawer>
      {getCNByIdLoading ? (
        //  Loader
        <ViewLoadingSkull
          isQuickView={isQuickView}
          fixedMenuBar={fixedMenuBar}
        />
      ) : (
        <div className="ant-row">
          <div
            className={`${window.location.href.includes("/sales/credit-note/view") ||
              window.location.href.includes("/sales/return-slip/view")
              ? (fixedMenuBar
                ? "ant-col-md-16"
                : "ant-col-md-17")
              : "ant-col-md-24"
              } ant-col-xs-24`}
          >
            <div
              className={`view-left__wrapper ${isQuickView ? "" : "is-page-view"
                }`}
            >
              <ViewCreditNoteHeader
                currentCN={currentCN}
                syncSOLoading={syncSOLoading}
                syncSO={syncSO}
                user={user}
                isReturnSlip={isReturnSlip}
                history={history}
                updateCNStatusLoading={updateCNStatusLoading}
                currentUpdate={currentUpdate}
                setCurrentUpdate={setCurrentUpdate}
                setSendForApprovalConfirmation={setSendForApprovalConfirmation}
                cancellationReason={cancellationReason}
                setCancellationReason={setCancellationReason}
                cancellationPop={cancellationPop}
                setCancellationPop={setCancellationPop}
                getCNById={getCNById}
                downloadDocument={downloadDocument}
                selectedCNId={selectedCNId}
                updateCNStatus={updateCNStatus}
                callback={callback}
                showTriggerNode={showTriggerNode}
                deleteCN={deleteCN}
                deleteCNLoading={deleteCNLoading}
                syncShopifySO={syncShopifySO}
                syncShopifySOLoading={syncShopifySOLoading}
                syncCN={syncCN}
                syncCNLoading={syncCNLoading}
                setShowEWaybillFormDrawer={setShowEWaybillFormDrawer}
                pushCreditNoteToBusy={pushCreditNoteToBusy}
                pushCreditNoteToBusyLoading={pushCreditNoteToBusyLoading}
                setShowPayment={setShowPayment}
                setLinkPayment={setLinkPayment}
                isTallyConnected={isTallyConnected}
                syncTallyInvoice={syncTallyInvoice}
                getCNByIdLoading={getCNByIdLoading}
                syncTallyInvoiceLoading={syncTallyInvoiceLoading}
              />
              <ViewCreditNoteDetails
                currentCN={currentCN}
                isReturnSlip={isReturnSlip}
                user={user}
                isTallyConnected={isTallyConnected}
              />
              <ViewCreditNoteEInvoiceInfo
                currentCN={currentCN}
                isReturnSlip={isReturnSlip}
                user={user}
                setCancellationReasonIRP={setCancellationReasonIRP}
                cancellationReasonIRP={cancellationReasonIRP}
                reasonOptions={reasonOptions}
                setCancellationRemark={setCancellationRemark}
                cancellationRemark={cancellationRemark}
                showErrorIRN={showErrorIRN}
                setShowErrorIRN={setShowErrorIRN}
                setCancellationRemarkPop={setCancellationRemarkPop}
                cancelIRP={cancelIRP}
                getCNById={getCNById}
                cancelIRPLoading={cancelIRPLoading}
                pushCnToIRPLoading={pushCnToIRPLoading}
                pushCnToIRP={pushCnToIRP}
              />
              {currentCN?.e_way_bills?.length && (
                <ViewDocumentEwb
                  eWayBills={currentCN?.e_way_bills}
                  actionCallback={() => {
                    getCNById(
                      Helpers.getTenantEntityPermission(
                        user?.user_tenants,
                        Helpers.permissionEntities.CREDIT_NOTE,
                        Helpers.permissionTypes.READ
                      ).join(",") || user?.tenant_info?.tenant_id,
                      currentCN.cn_id
                    );
                  }}
                />
              )}
              <ViewCreditNoteAddresses currentCN={currentCN} isReturnSlip={isReturnSlip} />
              <br />
              <CreditNoteLines
                cnLines={currentCN?.cn_lines?.map(item => ({ ...item, lineCustomFields: item?.custom_fields })) || []}
                selectedCN={currentCN}
                isReturnSlip={isReturnSlip}
                priceMasking={priceMasking}
                MONEY={MONEY}
                cfCreditNoteLine={currentCN?.cn_lines?.[0]?.custom_fields}
              />
              <ViewCreditNoteFooter
                currentCN={currentCN}
                fileList={fileList}
                getAttachmentByIdLoading={getAttachmentByIdLoading}
                updateAttachmentLoading={updateAttachmentLoading}
                handleFileChange={handleFileChange}
                isDataMaskingPolicyEnable={isDataMaskingPolicyEnable}
                isHideSellingPrice={isHideSellingPrice}
                MONEY={MONEY}
                user={user}
                isReturnSlip={isReturnSlip}
                updateAttachment={updateAttachment}
                getAttachmentById={getAttachmentById}
                setFileList={setFileList}
                setIsGetAttachment={setIsGetAttachment}
                selectedCN={selectedCN}
              />
            </div>
          </div>
          <ViewDocRightSection
            MONEY={MONEY}
            user={user}
            isQuickView={isQuickView}
            entityType={isReturnSlip ? "RETURN_SLIP" : "CREDIT_NOTE"}
            entityId={currentCN?.cn_id}
            workflowTimelineProps={{
              isEnabled: currentCN?.status?.toLowerCase() !== "draft",
              workflowSteps: currentCN?.workflow_steps || {},
              workflowStepId: currentCN?.workflow_step_id,
              loading: updateCNWorkflowStepLoading,
              updateStep: updateCNWorkflowStep,
              hideCase: HideAdhocApprovals(currentCN),
              callback: () => {
                getCNById(
                  Helpers.getTenantEntityPermission(
                    user?.user_tenants,
                    Helpers.permissionEntities.CREDIT_NOTE,
                    Helpers.permissionTypes.READ
                  ).join(",") || user?.tenant_info?.tenant_id,
                  currentCN.cn_id
                );
              },
            }}
            activityLogProps={{
              isEnabled: currentCN,
              entityName: isReturnSlip ? "Return Slip" : "Credit Note",
              entityType: isReturnSlip ? "return_slip" : "credit_note",
            }}
            linkedDocumentsInfo={getLinkedDocumentInfo({
              currentCN,
              isDataMaskingPolicyEnable,
              isHideCostPrice,
              isHideSellingPrice,
              setSelectedPayment,
              setShowViewPayout,
              showViewPayout,
            })}
          />
        </div>
      )}
      <PRZDrawer
        open={showEWaybillFormDrawer}
        onClose={() => setShowEWaybillFormDrawer(false)}
        width="520px"
        headerWidth="470px"
        title="Create e-Way Bill"
      >
        <EwaybillForm
          drawermode
          selectedDocument={currentCN}
          selectedDocType="CNT"
          documentDate={currentCN?.cn_date}
          selectedDocumentId={currentCN?.cn_id}
          selectedDocumentNumber={currentCN?.credit_note_number}
          callback={() => {
            getCNById(
              Helpers.getTenantEntityPermission(
                user?.user_tenants,
                Helpers.permissionEntities.CREDIT_NOTE,
                Helpers.permissionTypes.READ
              ).join(",") || user?.tenant_info?.tenant_id,
              selectedCNId
            );
            setShowEWaybillFormDrawer(false);
          }}
        />
      </PRZDrawer>
    </div>
  );
}

const mapStateToProps = ({
  UserReducers,
  GRNReducers,
  CNReducers,
  AttachmentReducers,
  UnicommerceIntegrationReducers,
  ShopifyIntegrationReducers,
  ZohoIntegrationReducers,
  BusyIntegrationReducers,
  EinvoiceEwaybillReducers,
  TallyIntegrationReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  getCNByIdLoading: CNReducers.getCNByIdLoading,
  selectedCN: CNReducers.selectedCN,
  updateCNStatusLoading: CNReducers.updateCNStatusLoading,
  updateCNWorkflowStepLoading: CNReducers.updateCNWorkflowStepLoading,
  grnData: GRNReducers.grnData,
  deleteCNLoading: CNReducers.deleteCNLoading,
  selectedAttachment: AttachmentReducers.selectedAttachment,
  getAttachmentByIdLoading: AttachmentReducers.getAttachmentByIdLoading,
  updateAttachmentLoading: AttachmentReducers.updateAttachmentLoading,
  syncSOLoading: UnicommerceIntegrationReducers.syncSOLoading,
  syncCNLoading: ZohoIntegrationReducers.syncCNLoading,
  syncShopifySOLoading: ShopifyIntegrationReducers.syncShopifySOLoading,
  pushCreditNoteToBusyLoading:
    BusyIntegrationReducers.pushCreditNoteToBusyLoading,
  pushCnToIRPLoading: EinvoiceEwaybillReducers.pushCnToIRPLoading,
  cancelIRPLoading: EinvoiceEwaybillReducers.cancelIRPLoading,
  syncTallyInvoiceLoading: TallyIntegrationReducers.syncTallyInvoiceLoading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  updateCNStatus: (payload, callback) =>
    dispatch(CNActions.updateCNStatus(payload, callback)),
  deleteCN: (payload, callback) =>
    dispatch(CNActions.deleteCN(payload, callback)),
  updateCNWorkflowStep: (payload, callback) =>
    dispatch(CNActions.updateCNWorkflowStep(payload, callback)),
  getCNById: (tenantId, cnId, docType) =>
    dispatch(CNActions.getCNById(tenantId, cnId, docType)),
  getCNByIdSuccess: (selectedCN) =>
    dispatch(CNActions.getCNByIdSuccess(selectedCN)),
  getAttachmentById: (entityId, entityName, callback) =>
    dispatch(
      AttachmentActions.getAttachmentById(entityId, entityName, callback)
    ),
  updateAttachment: (payload, callback) =>
    dispatch(AttachmentActions.updateAttachment(payload, callback)),
  syncSO: (payload, callback) =>
    dispatch(UnicommerceIntegrationActions.syncSO(payload, callback)),
  syncShopifySO: (payload, callback) =>
    dispatch(ShopifyIntegrationActions.syncShopifySO(payload, callback)),
  syncCN: (payload, callback) =>
    dispatch(ZohoIntegrationActions.syncCN(payload, callback)),
  downloadDocument: (payload, document) =>
    dispatch(AnalyticsActions.downloadDocument(payload, document)),
  getActivityLogSuccess: (activityLog) =>
    dispatch(ActivityLogActions.getActivityLogSuccess(activityLog)),
  pushCreditNoteToBusy: (payload, callback) =>
    dispatch(BusyIntegrationActions.pushCreditNoteToBusy(payload, callback)),
  pushCnToIRP: (payload, callback) =>
    dispatch(EinvoiceEwaybillIntegrationActions.pushCnToIRP(payload, callback)),
  cancelIRP: (payload, callback) =>
    dispatch(EinvoiceEwaybillIntegrationActions.cancelIRP(payload, callback)),
  syncTallyInvoice: (payload, callback) =>
    dispatch(TallyIntegrationActions.syncTallyInvoice(payload, callback)),
  getActivityLog: (entityId, entityName, page, limit, callback) =>
    dispatch(
      ActivityLogActions.getActivityLog(
        entityId,
        entityName,
        page,
        limit,
        callback
      )
    ),
});

ViewCreditNote.propTypes = {
  user: PropTypes.any,
  selectedCN: PropTypes.any,
  getCNByIdSuccess: PropTypes.func,
  getCNById: PropTypes.func,
  getCNByIdLoading: PropTypes.bool,
  selectedCNId: PropTypes.any,
  updateCNStatus: PropTypes.func,
  updateCNStatusLoading: PropTypes.bool,
  updateCNWorkflowStep: PropTypes.func,
  updateCNWorkflowStepLoading: PropTypes.bool,
  grnData: PropTypes.any,
  history: PropTypes.func,
  deleteCNLoading: PropTypes.bool,
  deleteCN: PropTypes.func,
  match: PropTypes.func,
  callback: PropTypes.any,
  updateAttachment: PropTypes.any,
  getAttachmentById: PropTypes.any,
  getAttachmentByIdLoading: PropTypes.any,
  updateAttachmentLoading: PropTypes.any,
  selectedAttachment: PropTypes.any,
  syncSO: PropTypes.func,
  syncSOLoading: PropTypes.bool,
  syncShopifySO: PropTypes.func,
  syncShopifySOLoading: PropTypes.bool,
  syncCN: PropTypes.func,
  getActivityLogSuccess: PropTypes.func,
  syncCNLoading: PropTypes.bool,
  cancelIRP: PropTypes.func,
  cancelIRPLoading: PropTypes.bool,
  pushCnToIRPLoading: PropTypes.bool,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ViewCreditNote));
